import type { ComponentType, ReactNode } from 'react';

import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Container,
  Stack,
  Typography,
  alpha,
  keyframes,
} from '@mui/material';
import { ChevronDown, type LucideProps, WandSparklesIcon } from 'lucide-react';

import { onboardingOutlinedInputRootSx } from '@modules/_shared/components/create-transaction/onboardingFormStyles';
import {
  BRAND_GRADIENT,
  BRAND_GRADIENT_HOVER,
} from '@modules/_shared/components/image-upload/brandGradient';
import { useLocale } from '@shared/hooks/locale';

import {
  formFieldLucideIconColor,
  formFieldLucideIconProps,
} from '../_shared/components/form/formFieldLucideIcons';

export { BRAND_GRADIENT, BRAND_GRADIENT_HOVER };

export const OnboardingPrimaryActionButton = ({
  children,
  onClick,
  disabled,
  loading,
  endIcon,
  emphasis = false,
  letterSpacing,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  endIcon?: ReactNode;
  emphasis?: boolean;
  letterSpacing?: number;
}) => (
  <Button
    onClick={onClick}
    variant="contained"
    disabled={disabled}
    loading={loading}
    loadingPosition="end"
    endIcon={endIcon}
    sx={{
      textTransform: 'none',
      fontWeight: emphasis ? 700 : 600,
      letterSpacing,
      fontSize: { xs: 14, md: 15 },
      px: { xs: 2, md: 3 },
      py: { xs: 0.75, md: 1 },
      borderRadius: '8px',
      background: BRAND_GRADIENT,
      boxShadow: '0 4px 14px rgba(32,48,151,0.25)',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      '&:hover': {
        background: BRAND_GRADIENT_HOVER,
        boxShadow: '0 6px 18px rgba(32,48,151,0.32)',
        transform: 'translateY(-1px)',
      },
      '&.Mui-disabled': {
        background: BRAND_GRADIENT,
        color: 'common.white',
        opacity: 0.5,
        boxShadow: 'none',
      },
    }}
  >
    {children}
  </Button>
);

export const onboardingDeckOutlinedActionButtonSx = {
  alignSelf: 'flex-start',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  borderColor: 'var(--rax-blue700)',
  color: 'var(--rax-blue700)',
  '&:hover': {
    borderColor: 'var(--rax-blue700)',
    bgcolor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.06)',
  },
  '&.Mui-disabled': {
    borderColor: 'action.disabledBackground',
    color: 'action.disabled',
  },
  '& .MuiButton-loadingIndicator': {
    color: 'var(--rax-blue700)',
  },
} as const;

export const OnboardingAiTextAction = ({
  actionLabel,
  loadingLabel,
  error,
  loading,
  disabled,
  onClick,
}: {
  actionLabel: ReactNode;
  loadingLabel: ReactNode;
  error: ReactNode | null;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) => (
  <Stack spacing={1} sx={{ mb: 1.5 }}>
    <Button
      variant="outlined"
      size="small"
      color="primary"
      loading={loading}
      loadingPosition="start"
      startIcon={<WandSparklesIcon size={20} />}
      disabled={disabled}
      onClick={onClick}
      sx={onboardingDeckOutlinedActionButtonSx}
    >
      {actionLabel}
    </Button>
    {loading && (
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontSize: 12, ml: 0.5 }}
      >
        {loadingLabel}
      </Typography>
    )}
    {error != null && (
      <Alert severity="error" sx={{ py: 0 }}>
        {error}
      </Alert>
    )}
  </Stack>
);

export const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export const onboardingHighlightPulse = keyframes`
  0% { box-shadow: 0 0 0 0 transparent; border-color: transparent; }
  20% { box-shadow: 0 0 0 6px var(--rax-blue100); border-color: var(--rax-blue700); }
  100% { box-shadow: 0 0 0 0 transparent; border-color: var(--rax-divider); }
`;

export const Eyebrow = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="overline"
    sx={{
      display: 'inline-block',
      px: 1.5,
      py: 0.5,
      mb: { xs: 1, md: 2 },
      borderRadius: 999,
      bgcolor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.08)',
      color: 'var(--rax-blue700)',
      letterSpacing: 1,
      fontWeight: 700,
      fontSize: { xs: 11, md: 12 },
      lineHeight: 1.4,
    }}
  >
    {children}
  </Typography>
);

export const SlideHeading = ({
  children,
  accent,
}: {
  children: ReactNode;
  accent?: ReactNode;
}) => (
  <Typography
    variant="h2"
    sx={{
      fontWeight: 700,
      fontSize: { xs: 24, sm: 36, md: 48 },
      lineHeight: { xs: 1.2, md: 1.1 },
      letterSpacing: -0.5,
      mb: { xs: 1, md: 2 },
    }}
  >
    {children}
    {accent != null && (
      <>
        {' '}
        <Box
          component="span"
          sx={{
            background: BRAND_GRADIENT,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {accent}
        </Box>
      </>
    )}
  </Typography>
);

export const SlideLead = ({ children }: { children: ReactNode }) => (
  <Typography
    component="div"
    variant="h6"
    color="text.secondary"
    sx={{
      fontWeight: 400,
      maxWidth: 680,
      mx: 'auto',
      fontSize: { xs: 16, md: 18 },
      lineHeight: { xs: 1.45, md: 1.55 },
    }}
  >
    {children}
  </Typography>
);

export const SlideHeader = ({
  eyebrow,
  heading,
  headingAccent,
  lead,
}: {
  eyebrow: ReactNode;
  heading: ReactNode;
  headingAccent?: ReactNode;
  lead: ReactNode;
}) => (
  <Stack
    spacing={{ xs: 2, md: 2.5 }}
    sx={{
      alignItems: 'flex-start',
      mb: 3,
      animation: `${fadeInUp} 0.5s ease-out`,
    }}
  >
    <Eyebrow>{eyebrow}</Eyebrow>
    <SlideHeading accent={headingAccent}>{heading}</SlideHeading>
    <SlideLead>{lead}</SlideLead>
  </Stack>
);

export const OnboardingDeckBackdrop = () => (
  <>
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top: '-15%',
        right: '-10%',
        width: 500,
        height: 500,
        borderRadius: '50%',
        background:
          'radial-gradient(circle, rgba(86,190,219,0.18) 0%, transparent 70%)',
        filter: 'blur(80px)',
        pointerEvents: 'none',
      }}
    />
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        bottom: '-20%',
        left: '-12%',
        width: 600,
        height: 600,
        borderRadius: '50%',
        background:
          'radial-gradient(circle, rgba(32,48,151,0.12) 0%, transparent 70%)',
        filter: 'blur(90px)',
        pointerEvents: 'none',
      }}
    />
  </>
);

export const ONBOARDING_DECK_BG =
  'linear-gradient(180deg, var(--rax-blue50) 0%, var(--rax-white) 40%, var(--rax-purple50) 100%)';

export const OnboardingDeckPage = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      background: ONBOARDING_DECK_BG,
    }}
  >
    <OnboardingDeckBackdrop />
    {children}
  </Box>
);

type OnboardingStepLayoutProps = {
  children: ReactNode;
  footer: ReactNode;
  maxWidth?: 'md' | 'lg';
  stepperSlot?: ReactNode;
};

export const OnboardingStepLayout = ({
  children,
  footer,
  maxWidth = 'lg',
  stepperSlot,
}: OnboardingStepLayoutProps) => (
  <OnboardingDeckPage>
    {stepperSlot && (
      <Box sx={{ pt: { xs: 2, md: 3 }, position: 'relative', zIndex: 1 }}>
        {stepperSlot}
      </Box>
    )}

    <Box
      sx={{
        flex: 1,
        py: { xs: 3, md: 5 },
        px: { xs: 2, md: 3 },
        pb: { xs: 20, md: 13 },
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Container maxWidth={maxWidth} disableGutters>
        {children}
      </Container>
    </Box>

    {footer}
  </OnboardingDeckPage>
);

export const onboardingInputIconProps: Pick<
  LucideProps,
  'size' | 'strokeWidth' | 'absoluteStrokeWidth' | 'color'
> = {
  ...formFieldLucideIconProps,
  color: formFieldLucideIconColor,
};

export const OnboardingInputIcon = ({
  icon: Icon,
  ...props
}: { icon: ComponentType<LucideProps> } & Partial<LucideProps>) => (
  <Icon {...onboardingInputIconProps} {...props} />
);

export const onboardingInputAdornmentSx = {
  mr: 1.25,
  color: formFieldLucideIconColor,
  flexShrink: 0,
  alignSelf: 'center',
  height: 'auto',
  maxHeight: 'none',
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    display: 'block',
    opacity: 1,
  },
};

export const onboardingInputInnerFieldSx = {
  borderRadius: 0,
  backgroundColor: 'transparent',
};

export const onboardingInputAutofillSx = {
  '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
    {
      WebkitBoxShadow:
        '0 0 0 1000px var(--mui-palette-background-paper) inset !important',
      WebkitTextFillColor: 'var(--mui-palette-text-primary) !important',
      caretColor: 'var(--mui-palette-text-primary)',
      borderRadius: 0,
      transition: 'background-color 5000s ease-in-out 0s',
    },
};

export const onboardingInputStretchSx = {
  alignSelf: 'stretch',
  boxSizing: 'border-box',
  ...onboardingInputInnerFieldSx,
  ...onboardingInputAutofillSx,
};

export const onboardingInputRootStretchSx = {
  alignItems: 'stretch',
};

export const onboardingTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    overflow: 'visible',
  },
  '& .MuiInputBase-input': onboardingInputInnerFieldSx,
  '& textarea.MuiInputBase-input': onboardingInputInnerFieldSx,
};

export const onboardingDeckPrimaryInputSlotSx = {
  fontSize: { xs: 18, md: 22 },
  fontWeight: 600,
  letterSpacing: -0.2,
  py: { xs: 1.5, md: 2 },
  pl: { xs: 1.5, md: 2 },
  pr: { xs: 1.5, md: 2 },
  '& input': {
    p: 0,
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit',
    ...onboardingInputStretchSx,
  },
  '& input::placeholder': {
    opacity: 0.5,
    fontWeight: 500,
  },
  '& textarea': {
    p: 0,
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    ...onboardingInputInnerFieldSx,
    ...onboardingInputAutofillSx,
  },
  '& .MuiInputAdornment-root': onboardingInputAdornmentSx,
};

export const onboardingDeckSearchInputSlotSx = {
  fontSize: { xs: 16, md: 18 },
  py: { xs: 1.25, md: 1.5 },
  pl: { xs: 1.5, md: 2 },
  pr: { xs: 1.5, md: 2 },
  ...onboardingInputRootStretchSx,
  '& input': {
    p: 0,
    fontSize: 'inherit',
    fontWeight: 500,
    ...onboardingInputStretchSx,
  },
  '& input::placeholder': {
    opacity: 0.55,
    fontWeight: 400,
  },
  '& .MuiInputAdornment-root': onboardingInputAdornmentSx,
};

const onboardingDeckOutlinedInputRootSx = {
  ...onboardingOutlinedInputRootSx,
  '& .MuiOutlinedInput-notchedOutline': {
    ...onboardingOutlinedInputRootSx['& .MuiOutlinedInput-notchedOutline'],
    transition: 'border-color 0.2s ease',
  },
  '&.Mui-focused': {
    ...onboardingOutlinedInputRootSx['&.Mui-focused'],
    boxShadow: `0 0 0 4px ${alpha('#56BEDB', 0.18)}`,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--rax-blue700)',
      borderWidth: 1.5,
    },
  },
};

export const onboardingDeckSelectTextFieldSx = {
  ...onboardingTextFieldSx,
  '& .MuiOutlinedInput-root': onboardingDeckOutlinedInputRootSx,
  '& .MuiSelect-select': {
    minHeight: 'unset !important',
    lineHeight: 1.25,
    fontSize: { xs: 18, md: 22 },
    fontWeight: 600,
    letterSpacing: -0.2,
    py: { xs: 1.5, md: 2 },
    pl: { xs: 1.5, md: 2 },
    pr: { xs: 4.5, md: 5 },
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
  },
};

export const onboardingDeckOutlinedTextFieldSx = {
  ...onboardingTextFieldSx,
  '& .MuiOutlinedInput-root': {
    ...onboardingDeckOutlinedInputRootSx,
    '&.Mui-error': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'error.main',
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 4px rgba(var(--mui-palette-error-mainChannel) / 0.15)`,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'error.main',
        },
      },
    },
  },
  '& .MuiFormHelperText-root': {
    ml: 0.5,
    mt: 0.75,
    fontSize: 12,
  },
};

export const onboardingDeckCompactInputSlotSx = {
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: 0,
  py: 1.125,
  pl: 1.25,
  pr: 1.25,
  ...onboardingInputRootStretchSx,
  '& input': {
    p: 0,
    fontSize: 'inherit',
    fontWeight: 'inherit',
    ...onboardingInputStretchSx,
  },
  '& .MuiInputAdornment-root': onboardingInputAdornmentSx,
};

export const onboardingDeckCompactOutlinedTextFieldSx = {
  ...onboardingDeckOutlinedTextFieldSx,
  '& .MuiOutlinedInput-root': {
    ...onboardingDeckOutlinedTextFieldSx['& .MuiOutlinedInput-root'],
    borderRadius: '10px',
  },
};

export const onboardingSkipButtonSx = {
  fontSize: 12,
  color: 'text.secondary',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  transition: 'opacity 0.15s',
  '&:hover': { color: 'text.primary' },
} as const;

export const onboardingDeckCompactSelectTextFieldSx = {
  ...onboardingDeckCompactOutlinedTextFieldSx,
  '& .MuiSelect-select': {
    minHeight: 'unset !important',
    lineHeight: 1.25,
    fontSize: 14,
    fontWeight: 500,
    py: 1.125,
    pl: 1.25,
    pr: 3.5,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-icon': {
    right: 8,
    top: 'calc(50% - 8px)',
    color: 'text.secondary',
    pointerEvents: 'none',
    transition: 'transform 0.15s ease',
  },
};

export const OnboardingSelectChevronIcon = (props: { className?: string }) => (
  <ChevronDown size={16} strokeWidth={2} {...props} />
);

export const onboardingDeckFieldLabelSx = (error: boolean) => ({
  display: 'block',
  mb: 0.75,
  fontSize: 14,
  fontWeight: 600,
  color: error ? 'error.main' : 'text.primary',
  letterSpacing: 0.1,
});

export const onboardingDeckSocialInputSlotSx = {
  fontSize: { xs: 15, md: 16 },
  fontWeight: 500,
  py: { xs: 1, md: 1.25 },
  pl: { xs: 1.25, md: 1.5 },
  pr: { xs: 1.5, md: 2 },
  '& input': {
    p: 0,
    fontSize: 'inherit',
    fontWeight: 'inherit',
    ...onboardingInputStretchSx,
  },
  '& input::placeholder': {
    opacity: 0.45,
    fontWeight: 400,
  },
  '& .MuiInputAdornment-root': { ...onboardingInputAdornmentSx, mr: 1 },
};

export const onboardingPreviewIconButtonSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: '10px',
  color: 'text.primary',
  bgcolor: 'background.paper',
  '&:hover': { bgcolor: alpha('#56BEDB', 0.08) },
} as const;

export const onboardingPreviewRemoveIconButtonSx = {
  ...onboardingPreviewIconButtonSx,
  color: 'text.secondary',
  '&:hover': { bgcolor: 'action.hover' },
} as const;

export const SkipStepButton = ({
  onClick,
  disabled = false,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => {
  const { t } = useLocale();
  return (
    <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-start' }}>
      <ButtonBase
        onClick={onClick}
        disabled={disabled}
        sx={{ ...onboardingSkipButtonSx, opacity: disabled ? 0.6 : 1 }}
      >
        {t('Skip for now')}
      </ButtonBase>
    </Box>
  );
};
